import { Button, LinkButton } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import type { CSSProperties, FC } from "react";
import React from "react";
import {
  IconArrows,
  IconClose,
  IconEye,
  IconLocker
} from "src/constants/icons";
import { cmsImageUrl } from "src/lib/cmsImageUrl";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import { toast } from "src/state/state";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { BottomSheet } from "src/ui/components/BottomSheet/BottomSheet";
import SignupCustomBloc from "./state/SignupCustomBloc";
import { healthInformationExchangeStepStyled } from "./HealthInformationExchangeStep.styled";

const {
  Screen,
  Content,
  ContentInner,
  Spacer,
  Hero,
  Title,
  Description,
  InfoCard,
  InfoRow,
  PrivacyNote,
  SheetTitle,
  SheetSection,
  SheetCloseButton
} = healthInformationExchangeStepStyled;

const WHITE_SHEET_STYLE = {
  "--dialog-background": "#fff"
} as CSSProperties;

const HERO_IMAGE = cmsImageUrl(
  "c09dad491f99f23630aa33cd4fc56277b5b31a8a-688x709.png"
);
const LAB_RESULTS_IMAGE = cmsImageUrl(
  "8bb49ab0a72a51d3f3d0181769d5ca6cbd196cfa-108x108.png"
);
const MEDICATIONS_IMAGE = cmsImageUrl(
  "453456bd1374bf1b60b5c16cb708b66443152f5d-108x108.png"
);

const HealthInformationExchangeStep: FC<{
  initialPrivacySheetOpen?: boolean;
}> = ({ initialPrivacySheetOpen = false }) => {
  const [, { nextStep, showSaving, hideSaving }] =
    useBlocNext(SignupCustomBloc);
  const [, { updateUserPreferences }] = useBlocNext(UserPreferencesCubit);
  const [isPrivacySheetOpen, setIsPrivacySheetOpen] = React.useState(
    initialPrivacySheetOpen
  );

  const { headingRef } = useFocusHeading();

  const handleContinue = React.useCallback(async () => {
    showSaving({
      text: translate("savingIndicator.info", { context: "healthInfoExchange" })
    });
    try {
      await updateUserPreferences({
        [UserPreferenceKeys.healthInformationExchangeCompleted]: true
      });
      void nextStep();
    } catch (error) {
      hideSaving();
      reportErrorSentry(error);
      toast.error("error.generic");
    }
  }, [showSaving, nextStep, hideSaving]);

  const closePrivacySheet = (): void => setIsPrivacySheetOpen(false);

  return (
    <Screen>
      <Content>
        <ContentInner>
          <Hero src={HERO_IMAGE} alt="" />

          <Title className="as-h3" ref={headingRef} {...getHeadingProps()}>
            {translate("healthInfoExchange.simplifyHealthcare")}
          </Title>
          <Spacer $size="md" />
          <Description className="as-body2">
            {translate("healthInfoExchange.description")}
          </Description>

          <Spacer $size="lg" />
          <InfoCard>
            <InfoRow>
              <img src={LAB_RESULTS_IMAGE} alt="" />
              <div>
                <h2 className="as-body2">
                  {translate("healthInfoExchange.labResults.title")}
                </h2>
                <p className="as-body2">
                  {translate("healthInfoExchange.labResults.description")}
                </p>
              </div>
            </InfoRow>
            <InfoRow>
              <img src={MEDICATIONS_IMAGE} alt="" />
              <div>
                <h2 className="as-body2">
                  {translate("healthInfoExchange.medications.title")}
                </h2>
                <p className="as-body2">
                  {translate("healthInfoExchange.medications.description")}
                </p>
              </div>
            </InfoRow>
          </InfoCard>

          <Spacer $size="md" />
          <PrivacyNote>
            <IconLocker />
            <p className="as-little">
              {translate("healthInfoExchange.privacyNote")}{" "}
              <LinkButton onPress={() => setIsPrivacySheetOpen(true)}>
                {translate("healthInfoExchange.learnMore")}
              </LinkButton>
            </p>
          </PrivacyNote>

          <Spacer $size="lg" />
          <Button
            theme="charcoal"
            onPress={() => void handleContinue()}
            fullWidth={true}
          >
            {translate("understood")}
          </Button>
          <Spacer $size="xl" />
        </ContentInner>
      </Content>

      <BottomSheet
        isOpen={isPrivacySheetOpen}
        onClose={closePrivacySheet}
        trackingName="HIE Privacy Info"
        style={WHITE_SHEET_STYLE}
      >
        <SheetCloseButton
          type="button"
          onClick={closePrivacySheet}
          aria-label={translate("healthInfoExchange.sheet.close")}
        >
          <IconClose />
        </SheetCloseButton>
        <SheetTitle>{translate("healthInfoExchange.sheet.title")}</SheetTitle>
        <SheetSection>
          <h3 className="as-body2">
            <IconArrows />
            {translate("healthInfoExchange.sheet.source.title")}
          </h3>
          <p className="as-body2">
            {translate("healthInfoExchange.sheet.source.description")}
          </p>
        </SheetSection>
        <SheetSection>
          <h3 className="as-body2">
            <IconEye />
            {translate("healthInfoExchange.sheet.access.title")}
          </h3>
          <p className="as-body2">
            {translate("healthInfoExchange.sheet.access.description")}
          </p>
        </SheetSection>
      </BottomSheet>
    </Screen>
  );
};

export default HealthInformationExchangeStep;
